// react
import { useContext } from 'react';

// types
import { CashInMethod, CashInMethodStatus, TransactionType } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// mui
import { Chip, Typography } from '@mui/material';

// ui
import { BtnMenu } from '../../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';
import { Time } from '../../../ui/Time/Time';

// dialogs
import { DialogDeleteCashInMethod } from '../../../dialogs/DialogDeleteCashInMethod/DialogDeleteCashInMethod';
import { DialogEditCashInMethod } from '../../../dialogs/DialogEditCashInMethod/DialogEditCashInMethod';
import { DialogUpdateCashInMethodStatus } from '../../../dialogs/DialogUpdateCashInMethodStatus/DialogUpdateCashInMethodStatus';

// utils
import { translateTransactionType } from '../../../../utils/translations';

// icons
import {
	DeleteOutlined as IconDelete,
	DoDisturbOutlined as IconDoDisturb,
	ModeEditOutlined as IconModeEdit,
	PowerSettingsNew as IconPowerSettingsNew,
} from '@mui/icons-material';

// props
export interface TableBasicCashInMethodsProps extends Omit<TableProps, 'headers'> {
	data: CashInMethod[];
	refetchCashInMethods?: () => void;
}

export const TableBasicCashInMethods = ({
	data = [],
	refetchCashInMethods,
	...props
}: TableBasicCashInMethodsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');

	const headers: TableHeader[] = [
		{
			label: t('name'),
			render: (name: string) => {
				return <Typography fontWeight={600}>{name}</Typography>;
			},
			value: 'name',
		},
		{
			label: t('status'),
			render: (status: CashInMethodStatus) => {
				return <ChipStatus status={status} />;
			},
			value: 'status',
		},
		{
			label: t('type'),
			render: (isDefault: boolean) => {
				return (
					<Chip
						color={isDefault ? 'info' : 'purple'}
						label={isDefault ? 'World Bank' : t('project')}
						size="small"
						variant="twoTone"
					/>
				);
			},
			value: 'isDefault',
		},
		{
			label: t('transactionTypes'),
			render: (transactionTypes: TransactionType[]) => {
				if (!transactionTypes || transactionTypes.length === 0) {
					return <Typography color="text.secondary">-</Typography>;
				}

				const typeNames = transactionTypes
					.sort((a, b) => a.localeCompare(b))
					.map((type) => translateTransactionType(type, t))
					.join(', ');

				return <Typography>{typeNames}</Typography>;
			},
			value: 'transactionTypes',
		},
		{
			label: t('created'),
			render: (created: string) => {
				return <Time date={created} />;
			},
			value: 'created',
		},
		{
			align: 'right',
			label: '',
			render: (name: string, header: TableHeader, cashInMethod: CashInMethod) => {
				const isDefault = cashInMethod.isDefault;

				if (isDefault) {
					return null;
				}

				const btnMenuItems = [];

				if (userProjectPermissions?.cashInMethod?.update) {
					btnMenuItems.push({
						children: t('editCashInMethod'),
						icon: <IconModeEdit />,
						onClick: () => {
							searchParams.set('dialog', 'editCashInMethod');
							searchParams.set('dialogCashInMethod', String(cashInMethod.id));
							setSearchParams(searchParams);
						},
					});

					// Add status update menu item
					const btnUpdateStatus = {
						children: t('deactivateCashInMethod'),
						icon: <IconDoDisturb color="error" />,
						onClick: () => {
							searchParams.set('dialog', 'updateCashInMethodStatus');
							searchParams.set('dialogCashInMethod', String(cashInMethod.id));
							searchParams.set('dialogStatus', 'DEACTIVATED');
							setSearchParams(searchParams);
						},
					};

					if (cashInMethod.status === 'DEACTIVATED') {
						btnUpdateStatus.children = t('activateCashInMethod');
						btnUpdateStatus.icon = <IconPowerSettingsNew color="success" />;
						btnUpdateStatus.onClick = () => {
							searchParams.set('dialog', 'updateCashInMethodStatus');
							searchParams.set('dialogCashInMethod', String(cashInMethod.id));
							searchParams.set('dialogStatus', 'ACTIVE');
							setSearchParams(searchParams);
						};
					}

					btnMenuItems.push(btnUpdateStatus);
				}

				if (userProjectPermissions?.cashInMethod?.delete) {
					btnMenuItems.push({
						children: t('deleteCashInMethod'),
						disabled: true,
						icon: <IconDelete color="error" />,
						onClick: () => {
							searchParams.set('dialog', 'deleteCashInMethod');
							searchParams.set('dialogCashInMethod', String(cashInMethod.id));
							setSearchParams(searchParams);
						},
					});
				}

				if (btnMenuItems.length === 0) {
					return null;
				}

				return <BtnMenu items={btnMenuItems} />;
			},
			value: 'created',
		},
	];

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogCashInMethod');
		searchParams.delete('dialogStatus');
		setSearchParams(searchParams);
	};

	return (
		<>
			<Table {...props} data={data} headers={headers} />
			<DialogDeleteCashInMethod
				isOpen={paramDialog === 'deleteCashInMethod'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchCashInMethods) {
						refetchCashInMethods();
					}
				}}
			/>
			<DialogEditCashInMethod
				isOpen={paramDialog === 'editCashInMethod'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchCashInMethods) {
						refetchCashInMethods();
					}
				}}
			/>
			<DialogUpdateCashInMethodStatus
				isOpen={paramDialog === 'updateCashInMethodStatus'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchCashInMethods) {
						refetchCashInMethods();
					}
				}}
			/>
		</>
	);
};
